import type { SxProps } from '@mui/material';
import { alpha, type CSSObject, type Theme } from '@mui/material/styles';
import type { SysTokens } from '@ovComponents/0-tokens/sys/sysTokens';

/** Plain style object safe for use inside `sx={[...]}` arrays. */
type ReconFlagsDrawerStyle = CSSObject;

export type ReconFlagsDetailDrawerTokens = {
  /** Horizontal inset for text blocks; body uses `px: 0` so rules and tabs stay full width. */
  blockHorizontalInset: ReconFlagsDrawerStyle;
  summaryTitleRow: SxProps;
  prioritySwatch: (prioritySwatchColor: string) => SxProps;
  metadataRows: SxProps;
  metadataLabel: SxProps;
  metadataValue: SxProps;
  metadataTypePill: SxProps;
  sectionDivider: SxProps;
  wireframeNote: SxProps;
  tabsRoot: SxProps;
  scrollRegion: SxProps;
  notesTabPane: SxProps;
  notesTabHistory: SxProps;
  notesTabCompose: SxProps;
  sectionHeading: SxProps;
  issueCalloutRoot: SxProps;
  issueCalloutTitleRow: SxProps;
  issueCalloutIcon: SxProps;
  issueCalloutTitle: SxProps;
  issueCalloutBody: SxProps;
  overviewRowGrid: SxProps;
  /** Full-bleed rule between overview sections (breaks out of horizontal inset). */
  overviewSectionDivider: SxProps;
  rawJsonPre: SxProps;
  footerStack: SxProps;
  /** Full-width rule between fixed notes and footer actions. */
  footerSectionDivider: SxProps;
  footerRow: ReconFlagsDrawerStyle;
};

/**
 * Module-local layout and surface tokens for the recon flags detail drawer.
 * Isolated here for a future move into the shared component library.
 */
export const getReconFlagsDetailDrawerTokens = (
  sys: SysTokens,
): ReconFlagsDetailDrawerTokens => {
  const criticalSurface =
    sys.color.supportNegativeOneVariant ??
    alpha(sys.color.negative ?? '#b3261e', 0.12);
  const criticalOnSurface =
    sys.color.supportNegativeOneOnVariant ??
    sys.color.onNegative ??
    sys.color.negative ??
    '#7f1d1d';

  /** Issue summary callout — saturated banner, dark border, on-surface typography (design). */
  const issueCalloutFg = sys.color.negative;
  const issueCalloutBg = sys.color.negativeVariant ?? '#e95d7e';
  const issueCalloutBodyColor = sys.color.onSurface ?? '#262626';
  const issueCalloutBorderColor = sys.color.negativeVariantPressed ?? '#9b2c3e';

  return {
    blockHorizontalInset: {
      px: 2,
      boxSizing: 'border-box',
    },
    summaryTitleRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1.5,
      mb: 1.5,
    },
    prioritySwatch: (prioritySwatchColor: string) => ({
      width: 10,
      height: 10,
      borderRadius: 0.5,
      flexShrink: 0,
      bgcolor: prioritySwatchColor,
    }),
    metadataRows: {
      display: 'flex',
      flexDirection: 'column',
      gap: 0.75,
    },
    metadataLabel: {
      color: sys.color.onSurfaceVariant,
      minWidth: 72,
    },
    metadataValue: {
      color: sys.color.onSurface,
      wordBreak: 'break-word',
    },
    metadataTypePill: {
      display: 'inline-block',
      px: 1,
      py: 0.25,
      borderRadius: 1,
      bgcolor: criticalSurface,
      color: criticalOnSurface,
    },
    sectionDivider: {
      width: '100%',
      borderTop: `1px solid ${sys.color.outlineVariant}`,
      my: 2,
    },
    wireframeNote: {
      color: sys.color.onSurfaceVariant,
      typography: 'caption',
      lineHeight: 1.45,
    },
    tabsRoot: {
      flexShrink: 0,
      width: '100%',
      minHeight: 40,
      borderBottom: 1,
      borderColor: 'divider',
      position: 'sticky',
      top: 0,
      zIndex: 1,
      bgcolor: sys.color.background,
      '& .MuiTab-root': {
        minHeight: 40,
        py: 0.5,
        textTransform: 'none',
      },
      '& .Mui-selected': {
        color: `${sys.color.primary} !important`,
      },
      '& .MuiTabs-indicator': {
        height: 3,
        bgcolor: sys.color.primary,
      },
    },
    scrollRegion: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      pt: 2,
      pb: 1,
    },
    notesTabPane: {
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    notesTabHistory: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      pt: 2,
      pb: 1,
    },
    notesTabCompose: {
      flexShrink: 0,
      borderTop: `1px solid ${sys.color.outlineVariant}`,
      pt: 1.5,
      pb: 1,
      bgcolor: sys.color.background,
    },
    sectionHeading: {
      mb: 1,
    },
    issueCalloutRoot: {
      p: 1.5,
      borderRadius: 1,
      border: `1px solid ${issueCalloutBorderColor}`,
      bgcolor: issueCalloutBg,
      mb: 0,
    },
    issueCalloutTitleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      mb: 0.75,
    },
    issueCalloutIcon: {
      flexShrink: 0,
      fontSize: 12,
      color: issueCalloutFg,
    },
    issueCalloutTitle: {
      color: issueCalloutFg,
      fontWeight: 700,
      fontSize: '12px',
      lineHeight: 1.35,
      letterSpacing: '0.15px',
    },
    issueCalloutBody: {
      color: issueCalloutBodyColor,
      whiteSpace: 'pre-wrap',
    },
    overviewRowGrid: {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.2fr)',
      columnGap: 1.5,
      rowGap: 0.75,
      py: 0.35,
    },
    overviewSectionDivider: ((theme: Theme) => ({
      borderTop: `1px solid ${sys.color.outlineVariant}`,
      my: theme.spacing(2),
      mx: theme.spacing(-2),
      width: `calc(100% + ${theme.spacing(4)})`,
    })) as SxProps,
    rawJsonPre: {
      m: 0,
      p: 1.5,
      borderRadius: 1,
      bgcolor: sys.color.surfaceContainer,
      fontSize: 12,
      overflowX: 'auto',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
    footerStack: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      flexShrink: 0,
      minWidth: 0,
    },
    footerSectionDivider: {
      width: '100%',
      borderTop: `1px solid ${sys.color.outlineVariant}`,
    },
    footerRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
      width: '100%',
    },
  };
};
